import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  Monitor, Plus, Pencil, Trash2, Upload, Package, Loader2, Search,
  ChevronDown, ChevronUp, Eye, IndianRupee, RefreshCw, X, UserCircle, Wallet
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// ============== Package Form ==============
function PackageForm({ pkg, onSave, onCancel }) {
  const [form, setForm] = useState({
    name: pkg?.name || '',
    description: pkg?.description || '',
    adigital_bundle_id: pkg?.adigital_bundle_id || '',
    base_price: pkg?.base_price || '',
    validity_days: pkg?.validity_days || 30,
    channels: pkg?.channels?.join(', ') || '',
    is_active: pkg?.is_active !== false
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.adigital_bundle_id || !form.base_price) {
      toast.error('Name, ADIGITAL Bundle ID, and price are required'); return;
    }
    setSaving(true);
    try {
      await onSave({
        ...form,
        base_price: parseFloat(form.base_price),
        validity_days: parseInt(form.validity_days),
        channels: form.channels ? form.channels.split(',').map(c => c.trim()).filter(Boolean) : []
      });
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Package Name *</Label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. ADN Telugu HD" data-testid="iptv-pkg-name" /></div>
        <div><Label>ADIGITAL Bundle ID *</Label><Input value={form.adigital_bundle_id} onChange={e => setForm(p => ({ ...p, adigital_bundle_id: e.target.value }))} placeholder="UUID from ADIGITAL" data-testid="iptv-pkg-bundle-id" /></div>
      </div>
      <div><Label>Description</Label><Textarea value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Package description" rows={2} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Base Price (Rs.) *</Label><Input type="number" value={form.base_price} onChange={e => setForm(p => ({ ...p, base_price: e.target.value }))} placeholder="0.00" min="0" step="0.01" data-testid="iptv-pkg-price" /></div>
        <div><Label>Validity (Days)</Label><Input type="number" value={form.validity_days} onChange={e => setForm(p => ({ ...p, validity_days: e.target.value }))} min="1" max="365" /></div>
      </div>
      <div>
        <Label>Channels (comma-separated)</Label>
        <Textarea value={form.channels} onChange={e => setForm(p => ({ ...p, channels: e.target.value }))} placeholder="Channel 1, Channel 2..." rows={3} />
        <p className="text-xs text-slate-500 mt-1">Or use CSV upload after creating</p>
      </div>
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={form.is_active} onChange={e => setForm(p => ({ ...p, is_active: e.target.checked }))} id="pkg-active" />
        <Label htmlFor="pkg-active">Active</Label>
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" disabled={saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-iptv-pkg">
          {saving && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
          {pkg ? 'Update Package' : 'Create Package'}
        </Button>
      </DialogFooter>
    </form>
  );
}


// ============== Partner Pricing Dialog ==============
function PartnerPricingDialog({ pkg, token, onClose, onUpdated }) {
  const [tab, setTab] = useState('type');
  const [typeForm, setTypeForm] = useState({ customer_type: '', price: '' });
  const [partnerSearch, setPartnerSearch] = useState('');
  const [partnerResults, setPartnerResults] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerPrice, setPartnerPrice] = useState('');
  const [saving, setSaving] = useState(false);

  const searchPartners = async (q) => {
    if (!q || q.length < 2) return;
    const res = await fetch(`${API}/api/wallet/search-customers?q=${encodeURIComponent(q)}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (res.ok) { const data = await res.json(); setPartnerResults(data.customers || []); }
  };

  useEffect(() => {
    const t = setTimeout(() => searchPartners(partnerSearch), 300);
    return () => clearTimeout(t);
  }, [partnerSearch]); // eslint-disable-line

  const handleSaveTypePricing = async () => {
    if (!typeForm.customer_type || !typeForm.price) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/iptv/packages/${pkg.package_id}/pricing`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_type: typeForm.customer_type, price: parseFloat(typeForm.price) })
      });
      if (res.ok) { toast.success('Type pricing updated'); onUpdated(); setTypeForm({ customer_type: '', price: '' }); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const handleSavePartnerPricing = async () => {
    if (!selectedPartner || !partnerPrice) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/iptv/packages/${pkg.package_id}/partner-pricing`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ customer_id: selectedPartner.customer_id, price: parseFloat(partnerPrice) })
      });
      if (res.ok) { toast.success(`Price set for ${selectedPartner.company_name}`); onUpdated(); setSelectedPartner(null); setPartnerPrice(''); setPartnerSearch(''); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Network error'); }
    setSaving(false);
  };

  const existingType = pkg.custom_pricing || {};
  const existingPartner = pkg.partner_pricing || {};

  return (
    <DialogContent className="max-w-lg">
      <DialogHeader>
        <DialogTitle>Pricing - {pkg.name}</DialogTitle>
        <DialogDescription>Base price: Rs.{pkg.base_price} | Set pricing by type or per individual operator</DialogDescription>
      </DialogHeader>

      <div className="flex gap-1 bg-slate-100 rounded-lg p-1 mb-4">
        <button onClick={() => setTab('type')} className={`flex-1 py-1.5 rounded-md text-sm font-medium ${tab === 'type' ? 'bg-white shadow-sm' : 'text-slate-500'}`}>By Type</button>
        <button onClick={() => setTab('partner')} className={`flex-1 py-1.5 rounded-md text-sm font-medium ${tab === 'partner' ? 'bg-white shadow-sm' : 'text-slate-500'}`} data-testid="iptv-pricing-partner-tab">By Operator</button>
      </div>

      {tab === 'type' && (
        <div className="space-y-3">
          {Object.keys(existingType).length > 0 && (
            <div className="space-y-1">
              {Object.entries(existingType).map(([type, p]) => (
                <div key={type} className="flex items-center justify-between bg-slate-50 rounded-md p-2 text-sm">
                  <span className="capitalize font-medium">{type}</span>
                  <span>Rs.{p.price}</span>
                </div>
              ))}
            </div>
          )}
          <Select value={typeForm.customer_type} onValueChange={v => setTypeForm(p => ({ ...p, customer_type: v }))}>
            <SelectTrigger><SelectValue placeholder="Customer Type" /></SelectTrigger>
            <SelectContent>
              {['retail', 'partner', 'mso', 'bulk', 'corporate'].map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input type="number" placeholder="Price (Rs.)" value={typeForm.price} onChange={e => setTypeForm(p => ({ ...p, price: e.target.value }))} />
          <Button onClick={handleSaveTypePricing} disabled={saving} className="w-full">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />} Set Type Pricing
          </Button>
        </div>
      )}

      {tab === 'partner' && (
        <div className="space-y-3">
          {Object.keys(existingPartner).length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-slate-500">Individual Operator Prices</p>
              {Object.entries(existingPartner).map(([cid, p]) => (
                <div key={cid} className="flex items-center justify-between bg-slate-50 rounded-md p-2 text-sm">
                  <span className="font-medium">{p.customer_name || cid.slice(0, 8)}</span>
                  <span className="font-semibold">Rs.{p.price}</span>
                </div>
              ))}
            </div>
          )}
          {!selectedPartner ? (
            <div>
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <Input className="pl-9" placeholder="Search operator..." value={partnerSearch} onChange={e => setPartnerSearch(e.target.value)} data-testid="iptv-pricing-partner-search" />
              </div>
              {partnerResults.length > 0 && (
                <div className="border rounded-md mt-1 max-h-32 overflow-y-auto divide-y">
                  {partnerResults.map(c => (
                    <button key={c.customer_id} className="w-full text-left p-2 hover:bg-slate-50 text-sm" onClick={() => { setSelectedPartner(c); setPartnerPrice(''); }}>
                      <p className="font-medium">{c.company_name}</p>
                      <p className="text-xs text-slate-500">{c.contact_phone}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-slate-50 p-2 rounded-md flex items-center justify-between">
              <div className="text-sm">
                <p className="font-medium">{selectedPartner.company_name}</p>
                <p className="text-xs text-slate-500">{selectedPartner.contact_phone}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => setSelectedPartner(null)}><X className="h-4 w-4" /></Button>
            </div>
          )}
          {selectedPartner && (
            <>
              <Input type="number" placeholder="Price for this operator" value={partnerPrice} onChange={e => setPartnerPrice(e.target.value)} data-testid="iptv-partner-price-input" />
              <Button onClick={handleSavePartnerPricing} disabled={saving} className="w-full" data-testid="iptv-partner-pricing-save-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserCircle className="h-4 w-4 mr-2" />}
                Set Price for {selectedPartner.company_name}
              </Button>
            </>
          )}
        </div>
      )}
    </DialogContent>
  );
}


// ============== IPTV Wallet Top-Up Dialog ==============
function IPTVWalletTopUpDialog({ token, onClose, onSuccess }) {
  const [search, setSearch] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('credit');
  const [form, setForm] = useState({ amount: '', payment_mode: 'bank_transfer', transaction_ref: '', notes: '', description: '' });
  const [debitForm, setDebitForm] = useState({ amount: '', reason: '', notes: '', allow_negative: false });
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [walletInfo, setWalletInfo] = useState(null);

  const searchCustomers = async (q) => {
    if (!q || q.length < 2) return;
    setSearching(true);
    try {
      const res = await fetch(`${API}/api/wallet/search-customers?q=${encodeURIComponent(q)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { const data = await res.json(); setCustomers(data.customers || []); }
    } catch (err) { console.error(err); }
    setSearching(false);
  };

  useEffect(() => {
    const t = setTimeout(() => searchCustomers(search), 300);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const fetchIPTVBalance = async (customerId) => {
    try {
      const res = await fetch(`${API}/api/iptv/wallet/balance/${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) { const data = await res.json(); setWalletInfo(data); }
    } catch { setWalletInfo(null); }
  };

  const handleSubmit = async () => {
    if (mode === 'credit') {
      if (!selected || !form.amount || parseFloat(form.amount) <= 0) {
        toast.error('Select an operator and enter amount'); return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/iptv/wallet/manual-credit`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: selected.customer_id,
            amount: parseFloat(form.amount),
            description: form.description || `IPTV Manual Top-up (${form.payment_mode})`,
            payment_mode: form.payment_mode,
            transaction_ref: form.transaction_ref,
            notes: form.notes
          })
        });
        const data = await res.json();
        if (res.ok && data.success) {
          toast.success(`Rs.${parseFloat(form.amount).toFixed(2)} credited to ${selected.company_name}'s IPTV wallet`);
          onSuccess();
          onClose();
        } else { toast.error(data.detail || 'Credit failed'); }
      } catch { toast.error('Network error'); }
      setLoading(false);
    } else {
      if (!selected || !debitForm.amount || parseFloat(debitForm.amount) <= 0) {
        toast.error('Select an operator and enter amount'); return;
      }
      if (!debitForm.reason.trim()) {
        toast.error('Reason is required for a debit'); return;
      }
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/iptv/wallet/manual-debit`, {
          method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customer_id: selected.customer_id,
            amount: parseFloat(debitForm.amount),
            reason: debitForm.reason,
            notes: debitForm.notes,
            allow_negative: debitForm.allow_negative,
          })
        });
        const data = await res.json();
        if (res.ok && data.success) {
          toast.success(`Rs.${parseFloat(debitForm.amount).toFixed(2)} debited from ${selected.company_name}'s IPTV wallet`);
          onSuccess();
          onClose();
        } else { toast.error(data.detail || 'Debit failed'); }
      } catch { toast.error('Network error'); }
      setLoading(false);
    }
  };

  const isCredit = mode === 'credit';
  const currentBalance = walletInfo?.balance ?? 0;
  const submitDisabled = loading || !selected ||
    (isCredit ? !form.amount : (!debitForm.amount || !debitForm.reason.trim()));

  return (
    <DialogContent className="max-w-md" data-testid="iptv-wallet-adjust-dialog">
      <DialogHeader>
        <DialogTitle>IPTV Wallet — Adjust Balance</DialogTitle>
        <DialogDescription>
          {isCredit ? "Manually credit an operator's IPTV wallet" : "Debit (deduct) from operator's IPTV wallet — requires a reason"}
        </DialogDescription>
      </DialogHeader>
      <div className="space-y-3 py-2">
        {!selected ? (
          <div>
            <Label>Search Operator</Label>
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
              <Input className="pl-9" placeholder="Search by name or phone..." value={search} onChange={e => setSearch(e.target.value)} data-testid="iptv-topup-search" />
            </div>
            {searching && <p className="text-xs text-slate-400 mt-1">Searching...</p>}
            {customers.length > 0 && (
              <div className="border rounded-md mt-2 max-h-40 overflow-y-auto divide-y">
                {customers.map(c => (
                  <button key={c.customer_id} className="w-full text-left p-2.5 hover:bg-slate-50 text-sm" onClick={() => { setSelected(c); fetchIPTVBalance(c.customer_id); }}>
                    <p className="font-medium">{c.company_name || c.contact_name}</p>
                    <p className="text-xs text-slate-500">{c.contact_phone} | OTT Bal: Rs.{(c.wallet_balance || 0).toFixed(2)}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <div className="bg-indigo-50 p-3 rounded-lg flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">{selected.company_name || selected.contact_name}</p>
                <p className="text-xs text-slate-500">{selected.contact_phone}</p>
                {walletInfo && <p className="text-xs text-indigo-600 font-semibold mt-1">IPTV Balance: Rs.{walletInfo.balance?.toFixed(2)}</p>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setSelected(null); setWalletInfo(null); }}><X className="h-4 w-4" /></Button>
            </div>
          </div>
        )}
        {selected && (
          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="credit" data-testid="iptv-tab-credit">Credit (Add)</TabsTrigger>
              <TabsTrigger value="debit" className="data-[state=active]:bg-red-50 data-[state=active]:text-red-700" data-testid="iptv-tab-debit">Debit (Deduct)</TabsTrigger>
            </TabsList>
            <TabsContent value="credit" className="space-y-3 pt-3">
              <div>
                <Label>Amount (Rs.) *</Label>
                <Input type="number" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} placeholder="Enter amount" min="1" data-testid="iptv-topup-amount" />
              </div>
              <div className="flex gap-2 flex-wrap">
                {[500, 1000, 2000, 5000, 10000].map(amt => (
                  <Button key={amt} variant="outline" size="sm" onClick={() => setForm(p => ({ ...p, amount: String(amt) }))} className="text-xs">Rs.{amt}</Button>
                ))}
              </div>
              <div>
                <Label>Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={v => setForm(p => ({ ...p, payment_mode: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="neft">NEFT/RTGS</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Transaction Reference</Label><Input value={form.transaction_ref} onChange={e => setForm(p => ({ ...p, transaction_ref: e.target.value }))} placeholder="UTR / Cheque No" /></div>
              <div><Label>Notes</Label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Optional notes" /></div>
            </TabsContent>
            <TabsContent value="debit" className="space-y-3 pt-3">
              <div>
                <Label>Amount to Debit (Rs.) *</Label>
                <Input type="number" value={debitForm.amount} onChange={e => setDebitForm(p => ({ ...p, amount: e.target.value }))} placeholder="Enter amount" min="1" data-testid="iptv-debit-amount" />
                {debitForm.amount && parseFloat(debitForm.amount) > currentBalance && !debitForm.allow_negative && (
                  <p className="text-xs text-red-600 mt-1">⚠ Exceeds current IPTV balance (Rs.{currentBalance.toFixed(2)}). Enable "Allow negative balance" to proceed.</p>
                )}
              </div>
              <div>
                <Label>Reason *</Label>
                <Input value={debitForm.reason} onChange={e => setDebitForm(p => ({ ...p, reason: e.target.value }))} placeholder="e.g., Refund, Manual correction, Service closure" data-testid="iptv-debit-reason" />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={debitForm.notes} onChange={e => setDebitForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional context (optional)" />
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={debitForm.allow_negative} onChange={e => setDebitForm(p => ({ ...p, allow_negative: e.target.checked }))} data-testid="iptv-debit-allow-negative" />
                Allow negative wallet balance
              </label>
            </TabsContent>
          </Tabs>
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button onClick={handleSubmit} disabled={submitDisabled}
          className={isCredit ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
          data-testid={isCredit ? 'iptv-topup-submit' : 'iptv-debit-submit'}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Wallet className="h-4 w-4 mr-1" />}
          {isCredit ? 'Credit IPTV Wallet' : 'Debit IPTV Wallet'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}


// ============== Main Component ==============
export default function IPTVPackages() {
  const { token } = useAuth();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editPkg, setEditPkg] = useState(null);
  const [expandedPkg, setExpandedPkg] = useState(null);
  const [showCsvUpload, setShowCsvUpload] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [adigitalBundles, setAdigitalBundles] = useState([]);
  const [showBundles, setShowBundles] = useState(false);
  const [loadingBundles, setLoadingBundles] = useState(false);
  const [showPricing, setShowPricing] = useState(null);
  const [showWalletTopUp, setShowWalletTopUp] = useState(false);

  const headers = { 'Authorization': `Bearer ${token}` };

  const fetchPackages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/iptv/packages?active_only=false`, { headers });
      if (res.ok) { const data = await res.json(); setPackages(data.packages || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]); // eslint-disable-line

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const handleCreate = async (data) => {
    try {
      const res = await fetch(`${API}/api/iptv/packages`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });
      const result = await res.json();
      if (res.ok && result.success) { toast.success('Package created'); setShowForm(false); fetchPackages(); }
      else { toast.error(result.detail || 'Failed to create'); }
    } catch { toast.error('Connection error'); }
  };

  const handleUpdate = async (data) => {
    try {
      const res = await fetch(`${API}/api/iptv/packages/${editPkg.package_id}`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });
      const result = await res.json();
      if (res.ok && result.success) { toast.success('Package updated'); setEditPkg(null); fetchPackages(); }
      else { toast.error(result.detail || 'Failed to update'); }
    } catch { toast.error('Connection error'); }
  };

  const handleDelete = async (packageId) => {
    if (!window.confirm('Deactivate this package?')) return;
    try {
      const res = await fetch(`${API}/api/iptv/packages/${packageId}`, { method: 'DELETE', headers });
      if (res.ok) { toast.success('Package deactivated'); fetchPackages(); }
    } catch { toast.error('Connection error'); }
  };

  const handleCsvUpload = async () => {
    if (!csvFile || !showCsvUpload) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      const res = await fetch(`${API}/api/iptv/packages/${showCsvUpload.package_id}/channels/csv`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData
      });
      const result = await res.json();
      if (res.ok && result.success) { toast.success(result.message); setShowCsvUpload(null); setCsvFile(null); fetchPackages(); }
      else { toast.error(result.detail || 'Upload failed'); }
    } catch { toast.error('Connection error'); }
    finally { setUploading(false); }
  };

  const fetchAdigitalBundles = async () => {
    setLoadingBundles(true);
    try {
      const res = await fetch(`${API}/api/iptv/adigital/bundles`, { headers });
      if (res.ok) { const data = await res.json(); setAdigitalBundles(data.bundles || []); setShowBundles(true); }
      else { const err = await res.json(); toast.error(err.detail || 'Failed'); }
    } catch { toast.error('Connection error'); }
    finally { setLoadingBundles(false); }
  };

  const seedStateCities = async () => {
    try {
      const res = await fetch(`${API}/api/iptv/state-city/seed`, { method: 'POST', headers });
      const result = await res.json();
      if (res.ok && result.success) { toast.success(result.message); }
      else { toast.error(result.detail || 'Failed'); }
    } catch { toast.error('Connection error'); }
  };

  const filtered = packages.filter(p => !searchTerm || p.name?.toLowerCase().includes(searchTerm.toLowerCase()));

  if (loading) return (
    <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-indigo-600" /></div>
  );

  return (
    <div className="space-y-6" data-testid="iptv-packages-admin">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <Monitor className="h-5 w-5 text-indigo-600" /> IPTV Packages
          </h2>
          <p className="text-sm text-slate-500">{packages.length} packages configured</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowWalletTopUp(true)} data-testid="iptv-wallet-topup-admin-btn">
            <Wallet className="h-4 w-4 mr-1" /> IPTV Wallet Top-Up
          </Button>
          <Button variant="outline" size="sm" onClick={seedStateCities} data-testid="seed-state-city-btn">
            <RefreshCw className="h-4 w-4 mr-1" /> Sync State/City
          </Button>
          <Button variant="outline" size="sm" onClick={fetchAdigitalBundles} disabled={loadingBundles} data-testid="fetch-bundles-btn">
            {loadingBundles ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            ADIGITAL Bundles
          </Button>
          <Button onClick={() => { setShowForm(true); setEditPkg(null); }} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-iptv-pkg-btn">
            <Plus className="h-4 w-4 mr-1" /> Add Package
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative w-80">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input placeholder="Search packages..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-9" />
      </div>

      {/* Packages List */}
      <div className="space-y-3">
        {filtered.map(pkg => (
          <Card key={pkg.package_id} className={!pkg.is_active ? 'opacity-60' : ''}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm">{pkg.name}</span>
                    <Badge variant="outline" className={pkg.is_active ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}>
                      {pkg.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700">{pkg.channels?.length || 0} channels</Badge>
                  </div>
                  <div className="text-xs text-slate-500 mt-1 flex items-center gap-3">
                    <span>Rs.{pkg.base_price}</span>
                    <span>{pkg.validity_days} days</span>
                    <span className="font-mono text-[10px]">{pkg.adigital_bundle_id?.substring(0, 8)}...</span>
                  </div>
                  {pkg.description && <p className="text-xs text-slate-400 mt-1">{pkg.description}</p>}
                  {Object.keys(pkg.partner_pricing || {}).length > 0 && (
                    <div className="text-[10px] text-indigo-500 mt-1">
                      Operator pricing: {Object.values(pkg.partner_pricing).map((p, i) => <span key={i}>{p.customer_name}: Rs.{p.price} </span>)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setExpandedPkg(expandedPkg === pkg.package_id ? null : pkg.package_id)}>
                    {expandedPkg === pkg.package_id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowPricing(pkg)} data-testid={`pricing-${pkg.package_id}`}>
                    <IndianRupee className="h-4 w-4 text-green-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setShowCsvUpload(pkg)} data-testid={`csv-upload-${pkg.package_id}`}>
                    <Upload className="h-4 w-4 text-blue-600" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => { setEditPkg(pkg); setShowForm(false); }}>
                    <Pencil className="h-4 w-4 text-amber-600" />
                  </Button>
                  {pkg.is_active && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg.package_id)}>
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              </div>
              {expandedPkg === pkg.package_id && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-xs font-medium text-slate-600 mb-2">Channels ({pkg.channels?.length || 0}):</p>
                  <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
                    {(pkg.channels || []).map((ch, i) => (
                      <Badge key={i} variant="outline" className="text-[10px] bg-slate-50">{ch}</Badge>
                    ))}
                    {(!pkg.channels || pkg.channels.length === 0) && (
                      <p className="text-xs text-slate-400">No channels. Upload CSV to add.</p>
                    )}
                  </div>
                  <div className="mt-3 text-xs text-slate-500">
                    <p>Bundle ID: <span className="font-mono">{pkg.adigital_bundle_id}</span></p>
                    <p>Created: {pkg.created_at ? new Date(pkg.created_at).toLocaleDateString() : '-'}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <Card><CardContent className="p-8 text-center text-slate-400">
            <Package className="h-12 w-12 mx-auto mb-3 opacity-50" /><p>No IPTV packages found.</p>
          </CardContent></Card>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showForm || !!editPkg} onOpenChange={() => { setShowForm(false); setEditPkg(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editPkg ? 'Edit IPTV Package' : 'Create IPTV Package'}</DialogTitle>
            <DialogDescription>Configure an IPTV package linked to an ADIGITAL bundle</DialogDescription>
          </DialogHeader>
          <PackageForm pkg={editPkg} onSave={editPkg ? handleUpdate : handleCreate} onCancel={() => { setShowForm(false); setEditPkg(null); }} />
        </DialogContent>
      </Dialog>

      {/* Pricing Dialog */}
      <Dialog open={!!showPricing} onOpenChange={() => setShowPricing(null)}>
        {showPricing && <PartnerPricingDialog pkg={showPricing} token={token} onClose={() => setShowPricing(null)} onUpdated={() => { fetchPackages(); setShowPricing(null); }} />}
      </Dialog>

      {/* IPTV Wallet Top-Up Dialog */}
      <Dialog open={showWalletTopUp} onOpenChange={setShowWalletTopUp}>
        {showWalletTopUp && <IPTVWalletTopUpDialog token={token} onClose={() => setShowWalletTopUp(false)} onSuccess={fetchPackages} />}
      </Dialog>

      {/* CSV Upload Dialog */}
      <Dialog open={!!showCsvUpload} onOpenChange={() => { setShowCsvUpload(null); setCsvFile(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Upload Channels CSV</DialogTitle>
            <DialogDescription>Upload CSV with channel names for "{showCsvUpload?.name}"</DialogDescription>
          </DialogHeader>
          <div className="py-3">
            <Input type="file" accept=".csv" onChange={e => setCsvFile(e.target.files[0])} data-testid="csv-file-input" />
            {csvFile && <p className="text-xs text-slate-500 mt-2">{csvFile.name} ({(csvFile.size / 1024).toFixed(1)} KB)</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCsvUpload(null); setCsvFile(null); }}>Cancel</Button>
            <Button onClick={handleCsvUpload} disabled={!csvFile || uploading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="upload-csv-btn">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} Upload
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ADIGITAL Bundles Reference */}
      <Dialog open={showBundles} onOpenChange={setShowBundles}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>ADIGITAL Bundles Reference</DialogTitle>
            <DialogDescription>Copy the bundle_id when creating a local package</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {adigitalBundles.map(b => (
              <div key={b.bundle_id} className="p-3 border rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{b.name}</span>
                  <Badge variant="outline">{b.channel_names?.length || 0} ch</Badge>
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <code className="text-[10px] bg-slate-100 px-2 py-0.5 rounded font-mono">{b.bundle_id}</code>
                  <Button variant="ghost" size="sm" className="h-6 text-xs" onClick={() => { navigator.clipboard.writeText(b.bundle_id); toast.success('Copied'); }}>Copy</Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
